import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { message } from '@client/components/_ui/feedback/message';
import { confirmAction } from '@client/components/_ui/feedback/modal';
import { PolicyHolder } from '@client/components/claims/lodgement/public/PolicyHolder';
import { PublicThemeProvider } from '@client/components/public-theme-provider';
import { useTrpc } from '@client/hooks/api/useTrpc';
import {
    clearPublicClaimProgressSnapshot,
    resolvePublicClaimSnapshotWriteKeys,
    usePublicClaimFormState,
    writePublicClaimProgressSnapshot,
    writePublicClaimQuestionnaireSnapshot,
} from '@client/hooks/business/claims/usePublicClaimFormState';
import { usePublicClaimSubmissionCheck } from '@client/hooks/business/claims/usePublicClaimSubmissionCheck';
import { useServerCheckpoint } from '@client/hooks/business/claims/useServerCheckpoint';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useCustomDocumentTitle } from '@client/hooks/ui/useCustomDocumentTitle';
import { useFavIcon } from '@client/hooks/ui/useFavIcon';
import { useQuestionnaireServiceStore } from '@client/stores/questionnaire/questionnaire-service-store';
import { trpc } from '@client/trpc/client';
import { PUBLIC_READINESS_QUERY_META } from '@client/trpc/query-meta';
import { debounce } from '@client/utils';
import { capture } from '@client/utils/analytics';
import { buildIncidentLocationSubmission } from '@client/utils/domain/incident-location';
import { parseBigintRouteParam } from '@client/utils/routing/parse-bigint-route-param';
import { QUESTIONNAIRE_MAPPED_FIELDS } from '@shared/constants';
import { decodeBase64Json } from '@shared/utils/core/base64-json';
import { filesToFileCollections } from '@shared/utils/core/file';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { TRPCClientError } from '@trpc/client';
import { Card, Col, ConfigProvider, Divider, Row, Typography, theme } from 'antd';
import type { AliasToken } from 'antd/es/theme/interface';
import Joi from 'joi';
import { ArrowLeft, CheckCircle, RotateCcw } from 'lucide-react';
import { DateTime } from 'luxon';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { useStore } from 'zustand';
import { preloadQuestionnaireLogo } from '../questionnaire-logo-preload';
import NotAvailable from './NotAvailable';
import { useDraftRestoreNavigation } from './useDraftRestoreNavigation';

const AdditionalInfo = lazy(() =>
    import('@client/components/claims/lodgement/AdditionalInfo').then((module) => ({
        default: module.AdditionalInfo,
    })),
);
const Conditions = lazy(() =>
    import('@client/components/claims/lodgement/Conditions').then((module) => ({ default: module.Conditions })),
);
const Confirmation = lazy(() =>
    import('@client/components/claims/lodgement/public/Confirmation').then((module) => ({
        default: module.Confirmation,
    })),
);
const PaymentDetails = lazy(() =>
    import('@client/components/claims/lodgement/public/PaymentDetails').then((module) => ({
        default: module.PaymentDetails,
    })),
);
const ProductDataEntryForm = lazy(() =>
    import('@client/components/claims/product-types-components/ProductDataEntryForm').then((module) => ({
        default: module.ProductDataEntryForm,
    })),
);
const Questionnaire = lazy(() =>
    import('@client/components/claims/questionnaire').then((module) => ({ default: module.Questionnaire })),
);
const SaveClaimDraftModal = lazy(() =>
    import('@client/components/claims/lodgement/public/SaveClaimDraftModal').then((module) => ({
        default: module.SaveClaimDraftModal,
    })),
);

type ClaimLodgementSearch = {
    draftId?: string;
    accessKey?: string;
    data?: string;
};

function ClaimLodgement() {
    const questionnaireStore = useQuestionnaireServiceStore();
    const { trpcClient } = useTrpc();
    const navigate = useNavigate();
    const { orgId, questionnaireId, productTypeId, pdsVersionId } = Route.useParams();
    const { draftId, accessKey, data } = Route.useSearch();
    const {
        store: formStore,
        loading,
        readinessError,
        retryReadiness,
    } = usePublicClaimFormState({
        orgId: orgId,
        pdsVersionId,
        questionnaireId: questionnaireId,
        productTypeId: productTypeId,
        draftId,
        accessKey,
    });

    // Subscribe to reactive form store slices
    const activeForm = useStore(formStore, (s) => s.ui.activeForm);
    const questionnaireLogoUrl = useStore(formStore, (s) => s.ui.questionnaireLogoUrl);
    const questionnaireFaviconUrl = useStore(formStore, (s) => s.ui.questionnaireFaviconUrl);
    const backButtonLabel = useStore(formStore, (s) => s.ui.backButtonLabel);
    const backButtonUrl = useStore(formStore, (s) => s.ui.backButtonUrl);
    const uiTheme = useStore(formStore, (s) => s.ui.theme);
    const direction = useStore(formStore, (s) => s.ui.direction);
    const activeStep = useStore(formStore, (s) => s.ui.activeStep);
    const readOnly = useStore(formStore, (s) => s.getReadOnly());
    const claim = useStore(formStore, (s) => s.claim);
    const note = useStore(formStore, (s) => s.note);
    const decisions = useStore(formStore, (s) => s.decisions);
    const statuses = useStore(formStore, (s) => s.statuses);
    const storedDraftId = useStore(formStore, (s) => s.draftId);
    const storedAccessKey = useStore(formStore, (s) => s.accessKey);
    const productTypeDef = useStore(formStore, (s) => s.productTypeDef);
    const questionnaireDeclarationTitle = useStore(formStore, (s) => s.ui.questionnaireDeclarationTitle);
    const questionnaireDeclarationBody = useStore(formStore, (s) => s.ui.questionnaireDeclarationBody);
    const questionnaireConfirmationBody = useStore(formStore, (s) => s.ui.questionnaireConfirmationBody);
    const questionnaireConfirmationTemplateData = useStore(
        formStore,
        (s) => s.ui.questionnaireConfirmationTemplateData,
    );
    const claimGeneratedId = useStore(formStore, (s) => s.claimGeneratedId);
    const claimNotResultOfIllegalAct = useStore(
        formStore,
        (s) => s.claim.jsonData?.claimNotResultOfIllegalAct ?? false,
    );
    const additionalInfo = useStore(formStore, (s) => s.claim.additionalInfo ?? '');
    const questionnaireVersion = useStore(questionnaireStore, (s) => s.version);
    const currentPathSeqNo = useStore(questionnaireStore, (s) => s.currentPathSeqNo);
    const questionnaireDirection = useStore(questionnaireStore, (s) => s.direction);

    const { data: isPdsVersionEnabled } = trpc.pdsVersion.isPdsVersionEnabled.useQuery(
        { id: pdsVersionId, orgId },
        {
            initialData: true,
            placeholderData: true,
            staleTime: 30_000,
            meta: PUBLIC_READINESS_QUERY_META,
        },
    );
    const { mutateAsync: publicClaimSubmission } = trpc.claim.publicClaimSubmission.useMutation({
        retry: false,
        trpc: {
            context: { captcha: true, captchaSingleUse: true },
        },
    });

    const [saveClaimModalVisible, setSaveClaimModalVisible] = useState(false);
    // Defer the initial chunk load until the modal is first opened, but keep it
    // mounted thereafter so antd's Modal can play its close/fade-out animation
    // (unmounting on every close would drop the transition and reset form state).
    const hasOpenedSaveClaimModalRef = useRef(false);
    if (saveClaimModalVisible) {
        hasOpenedSaveClaimModalRef.current = true;
    }
    const [isInitialized, setIsInitialized] = useState(false);
    const [isDraftSaving, setIsDraftSaving] = useState(false);
    const [resetVersion, setResetVersion] = useState(0);
    const previousPersistedDraftIdRef = useRef<string | undefined>(draftId);

    useFavIcon(questionnaireFaviconUrl || questionnaireLogoUrl);

    const { service } = questionnaireStore.getState();
    const activeDraftId = storedDraftId ?? draftId;
    const storageScope = useMemo(
        () => ({
            orgId,
            pdsVersionId,
            questionnaireId,
            productTypeId,
        }),
        [orgId, pdsVersionId, questionnaireId, productTypeId],
    );
    const submissionId = useStore(formStore, (s) => s.claim.submissionId);
    const { alreadySubmitted, handleSubmissionConflict, dismissAlreadySubmitted } = usePublicClaimSubmissionCheck({
        orgId,
        submissionId,
        enabled: isInitialized && !loading && activeForm < 6,
    });
    // Silent vehicle-scan server checkpoint (see useServerCheckpoint). The ref holds the
    // checkpoint identity privately; invalidateCheckpoint drops it on "Start again" and
    // fences off any checkpoint save still in flight across that reset. beginCheckpointJourney
    // extends that fence to a card's whole multi-step flow — "Start again" sits in the page
    // chrome below and stays clickable while a card is mid-launch.
    const { beginCheckpointJourney, checkpointCredentialsRef, invalidateCheckpoint, persistServerCheckpoint } =
        useServerCheckpoint({
            formStore,
            questionnaireStore,
            loading,
            isInitialized,
            alreadySubmitted: Boolean(alreadySubmitted),
            urlDraftId: draftId,
            urlAccessKey: accessKey,
            // This flow's confirmation step — matches the `activeForm >= 6` guards below.
            submittedFormIndex: 6,
        });

    useEffect(() => {
        async function initializeForm() {
            if (loading || readinessError) {
                return;
            }

            const state = formStore.getState();
            if (state.claim.formData) {
                questionnaireStore.getState().initAndContinue(state.claim.formData, state.questionnaireDef);
            } else {
                questionnaireStore.getState().setQuestionnaireDef(state.questionnaireDef);
            }

            setIsInitialized(true);
        }

        void initializeForm();
    }, [loading, readinessError, formStore, questionnaireStore]);

    const persistProgress = useCallback((): boolean => {
        const formState = formStore.getState();
        // An already-submitted state means this tab's state has been lodged elsewhere —
        // re-persisting it would resurrect the snapshot the submission just cleared.
        if (loading || !isInitialized || formState.ui.activeForm >= 6 || alreadySubmitted) {
            return false;
        }

        const questionnaireState = questionnaireStore.getState();
        const questionnaireRoot = questionnaireState.service.root?.toJS();
        const savedAt = new Date().toISOString();
        // A silent vehicle-scan checkpoint stays out of formStore so a later
        // explicit save still creates an emailed draft. Its browser fallback is
        // nevertheless keyed to that checkpoint: after a post-mint server-save
        // failure, the completion callback can then restore the newer snapshot
        // (which includes the invitation) over the older server draft. Because the
        // checkpoint id is invisible to the URL, that write is mirrored to `latest`
        // too (see resolvePublicClaimSnapshotWriteKeys) — otherwise a bare-URL
        // reload would rewind to the pre-checkpoint state.
        const snapshotDraftId = formState.draftId ?? checkpointCredentialsRef.current?.draftId ?? draftId;
        const snapshotAccessKey = formState.accessKey ?? checkpointCredentialsRef.current?.accessKey ?? accessKey;
        const writeKeys = resolvePublicClaimSnapshotWriteKeys({
            formDraftId: formState.draftId,
            checkpointDraftId: checkpointCredentialsRef.current?.draftId,
            urlDraftId: draftId,
        });

        const progressSnapshot = {
            version: 1 as const,
            claim: {
                ...formState.claim,
                formData: questionnaireRoot,
                files: filesToFileCollections(questionnaireState.service.files),
            },
            note: formState.note,
            decisions: formState.decisions,
            statuses: formState.statuses,
            ui: {
                activeForm: formState.ui.activeForm,
                activeStep: formState.ui.activeStep,
                direction: formState.ui.direction,
            },
            draftId: snapshotDraftId,
            accessKey: snapshotAccessKey,
            savedAt,
        };
        const questionnaireSnapshot = {
            version: 1 as const,
            root: questionnaireRoot,
            currentPathSeqNo: questionnaireState.currentPathSeqNo,
            direction: questionnaireState.direction,
            savedAt,
        };

        // Attempt every key even if one write fails (no short-circuit), then report
        // whether the snapshot is fully durable.
        return writeKeys
            .map((writeKey) => {
                const progressWritten = writePublicClaimProgressSnapshot(storageScope, progressSnapshot, writeKey);
                const questionnaireWritten = writePublicClaimQuestionnaireSnapshot(
                    storageScope,
                    questionnaireSnapshot,
                    writeKey,
                );
                return progressWritten && questionnaireWritten;
            })
            .every(Boolean);
    }, [accessKey, alreadySubmitted, draftId, formStore, isInitialized, loading, questionnaireStore, storageScope]);

    useEffect(() => {
        persistProgress();
    }, [
        persistProgress,
        activeForm,
        activeStep,
        claim,
        note,
        decisions,
        statuses,
        direction,
        questionnaireVersion,
        currentPathSeqNo,
        questionnaireDirection,
    ]);

    useEffect(() => {
        const previousDraftId = previousPersistedDraftIdRef.current;
        if (previousDraftId && previousDraftId !== activeDraftId) {
            clearPublicClaimProgressSnapshot(storageScope, previousDraftId);
        }
        if (!activeDraftId) {
            previousPersistedDraftIdRef.current = undefined;
            return;
        }
        clearPublicClaimProgressSnapshot(storageScope);
        previousPersistedDraftIdRef.current = activeDraftId;
    }, [activeDraftId, storageScope]);

    useDraftRestoreNavigation({
        draftId,
        storedDraftId,
        storedAccessKey,
        orgId,
        pdsVersionId,
        questionnaireId,
        productTypeId,
    });

    const baseFontSize = uiTheme.fontSize || 16;
    const { defaultAlgorithm, defaultSeed } = theme;
    const defaultTokens = defaultAlgorithm(defaultSeed);
    const newTheme: Partial<AliasToken> = {
        ...defaultTokens,
        colorPrimaryHover: uiTheme.colorPrimary,
        colorPrimaryActive: uiTheme.colorPrimary,
        ...uiTheme,
    };

    const additionalData = useMemo(() => decodeBase64Json<{ insurerName?: string }>(data) ?? {}, [data]);

    useCustomDocumentTitle(additionalData.insurerName && `New claim - ${additionalData.insurerName}`);

    const { goToNextForm, goToPrevForm, updateClaim, incrementActiveStep, decrementActiveStep } = formStore.getState();

    const save = debounce(async () => {
        try {
            const isoDateOfLoss = service.getFieldValue(QUESTIONNAIRE_MAPPED_FIELDS.DATE_OF_LOSS) as string;
            // Explicitly mapped questionnaire fields win; the location card fills the gaps
            const incidentLocation = buildIncidentLocationSubmission(service);

            updateClaim((c) => {
                c.jsonData = {
                    ...c.jsonData,
                    questionnaireDeclarationTitle: formStore.getState().ui.questionnaireDeclarationTitle,
                    questionnaireDeclarationBody: formStore.getState().ui.questionnaireDeclarationBody,
                };
            });

            const currentClaim = formStore.getState().claim;
            // Hand the active draft's access key to submission so createClaim's draft
            // sweep reclaims the silent vehicle-scan checkpoint draft and its handoff
            // row at lodgement — both are keyed by this accessKey — instead of leaving
            // them (and the handoff's plaintext draft credentials) to expire days later.
            const activeAccessKey =
                formStore.getState().accessKey ?? checkpointCredentialsRef.current?.accessKey ?? accessKey;
            const result = await publicClaimSubmission({
                causeOfLoss: service.getFieldValue(QUESTIONNAIRE_MAPPED_FIELDS.CAUSE_OF_LOSS) as string,
                addressLine1OfLoss:
                    (service.getFieldValue(QUESTIONNAIRE_MAPPED_FIELDS.ADDRESS_LINE_1_OF_LOSS) as string) ||
                    incidentLocation.addressLine1OfLoss,
                addressLine2OfLoss:
                    (service.getFieldValue(QUESTIONNAIRE_MAPPED_FIELDS.ADDRESS_LINE_2_OF_LOSS) as string) ||
                    incidentLocation.addressLine2OfLoss,
                suburbOfLoss:
                    (service.getFieldValue(QUESTIONNAIRE_MAPPED_FIELDS.SUBURB_OF_LOSS) as string) ||
                    incidentLocation.suburbOfLoss,
                stateOfLoss:
                    (service.getFieldValue(QUESTIONNAIRE_MAPPED_FIELDS.STATE_OF_LOSS) as string) ||
                    incidentLocation.stateOfLoss,
                postcodeOfLoss:
                    (service.getFieldValue(QUESTIONNAIRE_MAPPED_FIELDS.POSTCODE_OF_LOSS) as string) ||
                    incidentLocation.postcodeOfLoss,
                orgId: orgId,
                questionnaireId: questionnaireId,
                productTypeId: productTypeId,
                pdsVersionId: pdsVersionId,
                formData: service.root ? service.root.toJS() : undefined,
                files: filesToFileCollections(service.files),
                jsonData: {
                    ...currentClaim.jsonData,
                    questionnaireDeclarationTitle: formStore.getState().ui.questionnaireDeclarationTitle,
                    questionnaireDeclarationBody: formStore.getState().ui.questionnaireDeclarationBody,
                    incidentLocation: incidentLocation.incidentLocation,
                },
                contacts: currentClaim.contacts,
                policyNo: currentClaim.policyNo,
                phITC: currentClaim.phITC,
                phAccountBsb: currentClaim.phAccountBsb,
                phAccountNo: currentClaim.phAccountNo,
                additionalInfo: currentClaim.additionalInfo,
                phAccountName: currentClaim.phAccountName,
                phIsRegisteredForGst: currentClaim.phIsRegisteredForGst,
                productTypeCustomFieldsData: currentClaim.productTypeCustomFieldsData,
                claimOccurredAt: isoDateOfLoss ? DateTime.fromISO(isoDateOfLoss).toJSDate() : undefined,
                submissionId: currentClaim.submissionId,
                accessKey: activeAccessKey,
            });

            formStore.setState((draft) => {
                draft.claimId = Number(result.id);
                draft.claimGeneratedId = result.generatedId || undefined;
                draft.claimAccessKey = result.accessKey || undefined;
                draft.ui.questionnaireConfirmationTemplateData = result.templatePayload.data || undefined;
            });

            capture('claim_lodgement_submitted', { entity_id: result.id.toString() });

            clearPublicClaimProgressSnapshot(storageScope);
            if (activeDraftId) {
                clearPublicClaimProgressSnapshot(storageScope, activeDraftId);
            }
            if (draftId && draftId !== activeDraftId) {
                clearPublicClaimProgressSnapshot(storageScope, draftId);
            }

            goToNextForm();
        } catch (e) {
            if (e instanceof TRPCClientError && e.data?.code === 'CONFLICT') {
                // Another tab/browser already lodged this state (CUR-398) — flip to the
                // "claim already submitted" panel instead of surfacing an error.
                await handleSubmissionConflict();
                return;
            }
            errorMessage.show(e);
        }
    });

    async function handleSaveDraft(userDetails: { email: string; name: string; phone?: string }) {
        try {
            message.loading('Saving...');
            setIsDraftSaving(true);

            formStore.setState((draft) => {
                draft.claim.orgId = orgId;
                draft.claim.pdsVersionId = pdsVersionId;
                draft.claim.formData = service.root ? service.root.toJS() : undefined;
                draft.claim.files = filesToFileCollections(service.files);
                draft.claim.productTypeId = productTypeId;
                draft.claim.questionnaireId = questionnaireId;
            });

            const updatedState = formStore.getState();
            const draftSaveResult = await trpcClient.claim.saveClaimDraft.mutate(
                {
                    formData: {
                        claim: updatedState.claim,
                        ui: updatedState.ui,
                        questionnaireDef: updatedState.questionnaireDef,
                        productTypeDef: updatedState.productTypeDef,
                        decisions: updatedState.decisions,
                        statuses: updatedState.statuses,
                        note: updatedState.note,
                    },
                    userDetails: {
                        email: userDetails.email,
                        name: userDetails.name,
                        phone: userDetails.phone || undefined,
                    },
                    // Reuse the vehicle-scan checkpoint's credentials when the form
                    // store has none, so this explicit save UPDATES that silent draft
                    // (its resume email is sent via the marker path) instead of minting
                    // a second draft the handoff's return token doesn't point at.
                    draftId: updatedState.draftId ?? checkpointCredentialsRef.current?.draftId,
                    accessKey: updatedState.accessKey ?? checkpointCredentialsRef.current?.accessKey,
                },
                { context: { captcha: true, captchaSingleUse: true } },
            );

            formStore.setState((draft) => {
                draft.draftId = draftSaveResult.draftId;
                draft.accessKey = draftSaveResult.accessKey;
            });
            checkpointCredentialsRef.current = {
                draftId: draftSaveResult.draftId,
                accessKey: draftSaveResult.accessKey,
            };

            navigate({
                to: '/public/$orgId/claim/lodgement/$pdsVersionId/$questionnaireId/$productTypeId',
                params: {
                    orgId,
                    pdsVersionId,
                    questionnaireId,
                    productTypeId,
                },
                search: (prev: ClaimLodgementSearch) => ({
                    ...prev,
                    draftId: draftSaveResult.draftId,
                    accessKey: draftSaveResult.accessKey,
                }),
                replace: true,
            });

            message.success('Saved.');
            setSaveClaimModalVisible(false);
        } catch (e) {
            errorMessage.show(e);
        } finally {
            setIsDraftSaving(false);
        }
    }

    function resetLodgementProgress() {
        clearPublicClaimProgressSnapshot(storageScope);
        if (activeDraftId) {
            clearPublicClaimProgressSnapshot(storageScope, activeDraftId);
        }
        if (draftId && draftId !== activeDraftId) {
            clearPublicClaimProgressSnapshot(storageScope, draftId);
        }
        // The silent vehicle-scan checkpoint lives only in a private ref — invisible to
        // activeDraftId and the URL — so clear its snapshot and drop the identity
        // explicitly. Otherwise the restarted journey would be persisted (and mirrored
        // to `latest`) under the old checkpoint identity: a reload would rehydrate the
        // cleared draft's credentials, and an explicit save would update the old silent
        // draft instead of starting a fresh one. invalidateCheckpoint also fences off a
        // checkpoint save still in flight, so a slow response cannot re-install the
        // discarded identity after this reset.
        const checkpointDraftId = checkpointCredentialsRef.current?.draftId;
        if (checkpointDraftId && checkpointDraftId !== activeDraftId && checkpointDraftId !== draftId) {
            clearPublicClaimProgressSnapshot(storageScope, checkpointDraftId);
        }
        invalidateCheckpoint();
        dismissAlreadySubmitted();

        formStore.getState().resetProgress({
            orgId,
            pdsVersionId,
            questionnaireId,
            productTypeId,
        });
        questionnaireStore.getState().reset();
        questionnaireStore.getState().setQuestionnaireDef(formStore.getState().questionnaireDef);
        setResetVersion((prev) => prev + 1);

        navigate({
            to: '/public/$orgId/claim/lodgement/$pdsVersionId/$questionnaireId/$productTypeId',
            params: {
                orgId,
                pdsVersionId,
                questionnaireId,
                productTypeId,
            },
            search: data ? { data } : {},
            replace: true,
        });
    }

    async function handleStartAgain() {
        const confirmed = await confirmAction({
            title: 'Start again',
            content: 'This will clear your saved progress on this device and restart the claim lodgement form.',
            okText: 'Start again',
            cancelText: 'Cancel',
        });

        if (!confirmed) {
            return;
        }

        resetLodgementProgress();
        message.success('Progress cleared.');
    }

    const showAlreadySubmittedPanel = !!alreadySubmitted && activeForm < 6;

    // A NOT_FOUND is authoritative: `getPublicProductType` raises it only when the enabled
    // organisation/PDS/questionnaire/product tuple no longer resolves, and a claim can never be
    // lodged against an invalid tuple. So it stays terminal even mid-session — an administrator
    // re-pointing the PDS version's questionnaire must not leave an already-initialised form
    // submit-capable against the stale tuple. Every other readiness failure (transport blip,
    // 5xx) is retryable: those must not unmount a mid-session lodgement once the tuple has been
    // satisfied — the hook's error report covers them, and the retry panel below covers a
    // first-load failure.
    const tupleNotFound = readinessError instanceof TRPCClientError && readinessError.data?.code === 'NOT_FOUND';

    if (!isPdsVersionEnabled || tupleNotFound) {
        return <NotAvailable />;
    }

    if (readinessError && !isInitialized) {
        return (
            <InlineQueryError
                className="flex-1"
                error={readinessError}
                onRetry={retryReadiness}
                retryable
                title="Could not load claim form"
            />
        );
    }

    // Hold the first render until the questionnaire theme has loaded — painting
    // the form chrome earlier shows default colours, then visibly snaps to the
    // org branding once the theme query resolves. Later refetches (e.g. the
    // draft-save navigation re-running the load) keep the dimmed loading
    // overlay below instead, so the form does not unmount mid-session.
    if (loading && !isInitialized) {
        return <Loading size="large" className="flex-1" />;
    }

    return (
        <PublicThemeProvider colorPrimary={uiTheme.colorPrimary}>
            <ConfigProvider
                theme={{
                    token: newTheme,
                }}
            >
                {hasOpenedSaveClaimModalRef.current && (
                    <Suspense fallback={null}>
                        <SaveClaimDraftModal
                            open={saveClaimModalVisible}
                            onClose={() => setSaveClaimModalVisible(false)}
                            onSave={handleSaveDraft}
                            loading={isDraftSaving}
                        />
                    </Suspense>
                )}

                <div className="crm-custom-theme flex-1 md:px-0 lg:p-4">
                    <Row justify="center" className="pb-10">
                        <Col xs={24} sm={16} md={14} lg={12} xl={10} className="max-w-3xl">
                            <Loading spinning={loading}>
                                {questionnaireLogoUrl && (
                                    // Fixed-height box: the logo's intrinsic size is unknown until it
                                    // loads, and letting it expand the flow shifts the whole form (the
                                    // page's dominant CLS source).
                                    <div className="mb-3 flex h-24 items-center justify-center text-center">
                                        <img
                                            src={questionnaireLogoUrl}
                                            className="m-auto max-h-full max-w-full"
                                            alt="Questionnaire logo"
                                            fetchPriority="high"
                                        />
                                    </div>
                                )}
                                <div
                                    className="flex justify-center font-semibold text-foreground"
                                    style={{ fontSize: `${baseFontSize * 1.5}px` }}
                                >
                                    Claim Lodgement
                                </div>
                                {(!!(backButtonLabel && backButtonUrl) ||
                                    (activeForm < 6 && !showAlreadySubmittedPanel)) && (
                                    <div className="mt-2 mb-2 flex flex-wrap justify-center gap-2 md:flex-nowrap md:justify-between">
                                        {backButtonLabel && backButtonUrl && (
                                            <Button
                                                type="primary"
                                                icon={<ArrowLeft />}
                                                onClick={() => {
                                                    // http(s) only — a bare uri() would let a stored
                                                    // javascript: URL execute on the app origin.
                                                    const { error } = Joi.string()
                                                        .uri({ scheme: ['http', 'https'] })
                                                        .validate(backButtonUrl);
                                                    if (error) {
                                                        message.error('Something went wrong. Please try again later.');
                                                    } else {
                                                        window.location.href = backButtonUrl;
                                                    }
                                                }}
                                            >
                                                {backButtonLabel}
                                            </Button>
                                        )}
                                        {activeForm < 6 && !showAlreadySubmittedPanel && (
                                            <Button icon={<RotateCcw />} onClick={() => void handleStartAgain()}>
                                                Start again
                                            </Button>
                                        )}
                                    </div>
                                )}
                                <Divider className="mt-2 mb-4" />
                                {showAlreadySubmittedPanel ? (
                                    <Card>
                                        <div className="m-auto flex max-w-lg flex-col items-center gap-3 p-4 text-center">
                                            <CheckCircle
                                                size={48}
                                                strokeWidth={1.5}
                                                className="text-success"
                                                aria-hidden
                                            />
                                            <Typography.Title level={4} className="mb-0!">
                                                Claim already submitted
                                            </Typography.Title>
                                            <div>
                                                This claim has already been submitted, most likely from another tab,
                                                browser or device.
                                                {alreadySubmitted?.generatedId && (
                                                    <>
                                                        {' '}
                                                        Your claim number is{' '}
                                                        <span className="font-semibold">
                                                            {alreadySubmitted.generatedId}
                                                        </span>
                                                        .
                                                    </>
                                                )}
                                            </div>
                                            <div>
                                                Submitting it again would create a duplicate claim. If you need to lodge
                                                a different claim, start a new one.
                                            </div>
                                            <div className="pt-2">
                                                <Button
                                                    type="primary"
                                                    size="large"
                                                    onClick={() => resetLodgementProgress()}
                                                >
                                                    Start a new claim
                                                </Button>
                                            </div>
                                        </div>
                                    </Card>
                                ) : (
                                    <Suspense fallback={<Loading size="large" className="py-10" />}>
                                        <div key={`${activeForm}-${resetVersion}`}>
                                            {activeForm === 0 && (
                                                <PolicyHolder
                                                    goBack={() => {}}
                                                    goForward={goToNextForm}
                                                    onSaveDraftOpen={() => setSaveClaimModalVisible(true)}
                                                    claim={claim}
                                                    updateClaim={updateClaim}
                                                    lodgement={{
                                                        orgId: String(orgId),
                                                        questionnaireId: String(questionnaireId),
                                                    }}
                                                />
                                            )}
                                            {activeForm === 1 && (
                                                <PaymentDetails
                                                    claim={claim}
                                                    goBack={goToPrevForm}
                                                    goForward={goToNextForm}
                                                    onSaveDraftOpen={() => setSaveClaimModalVisible(true)}
                                                    updateClaim={updateClaim}
                                                />
                                            )}
                                            {activeForm === 2 && (
                                                <ProductDataEntryForm
                                                    orgId={orgId}
                                                    productTypeDef={productTypeDef}
                                                    productTypeId={productTypeId}
                                                    claimData={claim}
                                                    goBack={goToPrevForm}
                                                    goForward={goToNextForm}
                                                    onSaveDraftOpen={() => setSaveClaimModalVisible(true)}
                                                    direction={direction}
                                                    updateClaim={updateClaim}
                                                />
                                            )}
                                            {activeForm === 3 && (
                                                <Questionnaire
                                                    onSave={save}
                                                    readOnly={readOnly}
                                                    goBack={goToPrevForm}
                                                    goNext={goToNextForm}
                                                    onSaveDraftOpen={() => setSaveClaimModalVisible(true)}
                                                    questionnaireStore={questionnaireStore}
                                                    displaySaveButton={false}
                                                    isInitialized={isInitialized}
                                                    isPublic
                                                    onActiveStepIncrement={incrementActiveStep}
                                                    onActiveStepDecrement={decrementActiveStep}
                                                    persistProgress={persistProgress}
                                                    persistServerCheckpoint={persistServerCheckpoint}
                                                    beginCheckpointJourney={beginCheckpointJourney}
                                                    lodgement={{
                                                        orgId: String(orgId),
                                                        questionnaireId: String(questionnaireId),
                                                        pdsVersionId: String(pdsVersionId),
                                                        productTypeId: String(productTypeId),
                                                        draftId: activeDraftId,
                                                        submissionId: submissionId ?? undefined,
                                                    }}
                                                />
                                            )}
                                            {activeForm === 4 && (
                                                <Conditions
                                                    onSave={save}
                                                    readOnly={readOnly}
                                                    goBack={goToPrevForm}
                                                    goNext={goToNextForm}
                                                    onSaveDraftOpen={() => setSaveClaimModalVisible(true)}
                                                    displaySaveButton={false}
                                                    questionnaireDeclarationTitle={questionnaireDeclarationTitle}
                                                    questionnaireDeclarationBody={questionnaireDeclarationBody}
                                                    claimNotResultOfIllegalAct={claimNotResultOfIllegalAct}
                                                    updateClaim={updateClaim}
                                                />
                                            )}
                                            {activeForm === 5 && (
                                                <AdditionalInfo
                                                    onSave={save}
                                                    readOnly={readOnly}
                                                    goBack={goToPrevForm}
                                                    additionalInfo={additionalInfo}
                                                    updateClaim={updateClaim}
                                                />
                                            )}

                                            {activeForm === 6 && (
                                                <Confirmation
                                                    claimGeneratedId={claimGeneratedId}
                                                    questionnaireConfirmationBody={questionnaireConfirmationBody}
                                                    questionnaireConfirmationTemplateData={
                                                        questionnaireConfirmationTemplateData
                                                    }
                                                />
                                            )}
                                        </div>
                                    </Suspense>
                                )}
                            </Loading>
                        </Col>
                    </Row>
                </div>
            </ConfigProvider>
        </PublicThemeProvider>
    );
}

export const Route = createFileRoute('/public/$orgId/claim/lodgement/$pdsVersionId/$questionnaireId/$productTypeId')({
    params: {
        parse(params) {
            return {
                pdsVersionId: parseBigintRouteParam(params.pdsVersionId),
                questionnaireId: parseBigintRouteParam(params.questionnaireId),
                productTypeId: parseBigintRouteParam(params.productTypeId),
            };
        },
    },
    beforeLoad: ({ context, params }): void => {
        // Warm all readiness reads while the public-org layout initialises. Fire-and-forget intentionally;
        // the existing consumers retain ownership of loading and error behaviour.
        void context.trpcUtils.org.getOrgSettings.prefetch(
            { orgId: params.orgId },
            { retry: false, staleTime: 30_000, meta: PUBLIC_READINESS_QUERY_META },
        );
        // The org's logo is this page's LCP element and its URL is only known from this
        // payload — once the prefetch settles, read the warmed cache and preload the image
        // so its download overlaps the render gates below. (`prefetch` + `getData`, not
        // `ensureData`: ensureData would return stale cache without refetching, silently
        // dropping the staleTime semantics this warm-up relies on.)
        // Same input as usePublicClaimFormState's fetch, so the warmed entry is the one it reads.
        const questionnaireInput = {
            id: params.questionnaireId,
            orgId: params.orgId,
            pdsVersionId: params.pdsVersionId,
            productTypeId: params.productTypeId,
        };
        context.trpcUtils.questionnaire.getPublicQuestionnaire
            .prefetch(questionnaireInput, { retry: false, staleTime: 30_000, meta: PUBLIC_READINESS_QUERY_META })
            .then(() =>
                preloadQuestionnaireLogo(
                    context.trpcUtils.questionnaire.getPublicQuestionnaire.getData(questionnaireInput)?.logoUrl,
                ),
            )
            .catch(() => {
                // Speculative warm-up only — the render-time query owns error handling.
            });
        void context.trpcUtils.productType.getPublicProductType.prefetch(
            {
                orgId: params.orgId,
                pdsVersionId: params.pdsVersionId,
                questionnaireId: params.questionnaireId,
                productTypeId: params.productTypeId,
            },
            { retry: false, staleTime: 30_000, meta: PUBLIC_READINESS_QUERY_META },
        );
        void context.trpcUtils.pdsVersion.isPdsVersionEnabled.prefetch(
            { id: params.pdsVersionId, orgId: params.orgId },
            { staleTime: 30_000, meta: PUBLIC_READINESS_QUERY_META },
        );
    },
    validateSearch: zodValidator(
        z.object({
            draftId: z.string().optional(),
            accessKey: z.string().optional(),
            data: z.string().optional(),
            // Set by the vehicle-inspection provider's completion redirect when a
            // same-tab claimant returns from the scan (VEHICLE_INSPECTION card).
            scan: z.string().optional(),
        }),
    ),
    component: ClaimLodgement,
});
